<template>
  <el-input
    v-bind="$attrs"
    ref="inputRef"
    :clearable="true"
    :class="[
      weight && weights[weight],
      color && colors[color],
      textSize && textSizes[textSize],
      width && widths[width],
    ]"
    class="app-input"
  >
    <template
      v-for="(_, scopedSlotName) in $slots"
      #[scopedSlotName]="slotData"
    >
      <slot :name="scopedSlotName" v-bind="slotData" />
    </template>

    <template v-for="(_, slotName) in $slots" #[slotName]>
      <slot :name="slotName" />
    </template>
  </el-input>
</template>

<script setup lang="ts">
import { ElInput } from 'element-plus'
import { ref } from 'vue'

type TextWeight = 'light' | 'regular' | 'medium' | 'semibold' | 'bold'
type TextColors = 'primary'
type TextSize = 'xs' | 'md' | 'sm' | 'lg' | 'xl' | 'xxl' | 'xxxl'
type Width = 'sm' | 'md' | 'lg' | 'xl'

defineProps<{
  weight?: TextWeight
  textSize?: TextSize
  color?: TextColors
  width?: Width
}>()

const inputRef = ref()

const widths: Record<Width, string> = {
  sm: 'tw-w-[100px]',
  md: 'tw-w-[200px]',
  lg: 'tw-w-[300px]',
  xl: 'tw-w-[400px]',
}

const weights: Record<TextWeight, string> = {
  light: 'tw-font-light',
  regular: 'tw-font-normal',
  medium: 'tw-font-medium',
  semibold: 'tw-font-semibold',
  bold: 'tw-font-bold',
}

const textSizes: Record<TextSize, string> = {
  xs: 'tw-text-xs',
  sm: 'tw-text-sm',
  md: 'tw-text-base',
  lg: 'tw-text-lg',
  xl: 'tw-text-xl',
  xxl: 'tw-text-2xl',
  xxxl: 'tw-text-3xl',
}

const colors: Record<TextColors, string> = {
  primary: 'app-text-primary',
}

defineExpose({
  inputRef,
})
</script>

<style scoped lang="scss">
.app-text-primary {
  :deep(.el-input__inner) {
    @apply tw-text-dynamic-primary;
  }
}
</style>
